import React, { useState } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useUser, useAuth, useSignUp, useClerk } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import { useOnboardingDataRedux } from '../../hooks/useOnboardingDataRedux';
import { createCustomerForTrial, getPaymentMethodDetails } from '../../api';
import { StepProps } from '../types/stepTypes';

interface TrialPaymentStepProps extends Omit<StepProps, 'onClose'> {
  selectedPlan: string;
  onClose?: () => void;
}

// Helper function to add timeout to any promise
function withTimeout<T>(promise: Promise<T>, timeoutMs: number, operation: string): Promise<T> {
  const timeoutPromise = new Promise<never>((_, reject) => {
    setTimeout(() => reject(new Error(`${operation} timed out after ${timeoutMs}ms`)), timeoutMs);
  });
  
  return Promise.race([promise, timeoutPromise]);
}

const TrialPaymentStep: React.FC<TrialPaymentStepProps> = ({
  currentStep,
  setCurrentStep,
  completedSteps,
  setCompletedSteps,
  selectedPlan,
  onClose
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const { user } = useUser();
  const { isLoaded, signUp } = useSignUp();
  const { isSignedIn, signOut } = useAuth();
  const clerk = useClerk();
  const { 
    updateData, 
    paymentData, 
    data: onboardingData, 
    resetData, 
    completeOnboarding,
    goBackToEditProfile,
    hasPaymentCompleted,
    shouldPreventPlanChange
  } = useOnboardingDataRedux();
  
  const [processing, setProcessing] = useState(false);
  const [setupSuccess, setSetupSuccess] = useState(paymentData.paymentStatus === 'completed');
  const [error, setError] = useState<string>('');
  const [cardComplete, setCardComplete] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const [accountCreationError, setAccountCreationError] = useState<string>('');
  const [accountCreationAttempted, setAccountCreationAttempted] = useState(false);
  const [cardBrand, setCardBrand] = useState<string>('');
  const [cardLast4, setCardLast4] = useState<string>('');

  // Get plan data from Redux onboarding data
  const planKey = onboardingData.selectedPlan || selectedPlan || 'standard';
  
  // Parse plan key to extract base plan type and billing cycle
  const isYearlyPlan = planKey.includes('_yearly');
  const billingCycle = isYearlyPlan ? 'yearly' : 'monthly';
  
  // Extract base plan type
  const basePlanType = planKey.replace('_yearly', '').replace('_monthly', '').toLowerCase();
  
  // Plan configurations
  const PLANS = {
    starter: { name: 'Starter', price: 2900, features: ['100 messages/month', 'Basic AI support'] },
    standard: { name: 'Standard', price: 4900, features: ['Unlimited messages', 'Priority support', 'Advanced AI features'] },
    pro: { name: 'Pro', price: 9900, features: ['Everything in Standard', 'Premium AI models', '24/7 support'] }
  };
  
  const basePlan = PLANS[basePlanType as keyof typeof PLANS] || PLANS['standard'];
  
  // Calculate actual price for display (with yearly discount)
  const actualPrice = isYearlyPlan 
    ? Math.round(basePlan.price * 12 * 0.8) // 20% discount for yearly
    : basePlan.price;
  
  const currentPlan = {
    ...basePlan,
    price: actualPrice,
    billingCycle,
    planKey
  };

  const handleCardChange = (event: any) => {
    console.log('💳 Card change event:', event);
    setCardComplete(event.complete);
    
    // Capture card details from the UI
    if (event.brand) {
      setCardBrand(event.brand);
      console.log('💳 Card brand captured:', event.brand);
    }
    
    if (event.error) {
      setError(event.error.message);
    } else {
      setError('');
    }
  };

  const createUserAccountsAfterSetup = async (paymentMethodData: any = null) => {
    console.log('🔄 [TRIAL] Starting user account creation process...');
    console.log('🔍 [TRIAL] Input payment method data:', JSON.stringify(paymentMethodData, null, 2));
    console.log('🔍 [TRIAL] Current onboardingData:', JSON.stringify(onboardingData, null, 2));
    
    if (accountCreationAttempted) {
      console.log('⚠️ [TRIAL] Account creation already attempted, skipping...');
      return;
    }
    
    console.log('🔒 [TRIAL] Marking account creation as attempted');
    setAccountCreationAttempted(true);
    
    try {
      let clerkUserId;
      
      // Check if user is already signed in
      if (isSignedIn && user) {
        console.log('✅ [TRIAL] User is already signed in, using existing account:', user.id);
        clerkUserId = user.id;
        // Clear any previous account creation errors since we're using existing account
        setAccountCreationError('');
        setError('');
      } else {
        console.log('📧 [TRIAL] Starting Clerk account creation...');
        
        if (!signUp) {
          throw new Error('Clerk signUp not initialized');
        }

        // Generate a secure random password for trial user
        const randomPassword = Math.random().toString(36) + Math.random().toString(36) + '!1A';
        console.log('🔐 [TRIAL] Generated password for Clerk account');

        // Create the Clerk account
        const clerkResult = await signUp.create({
          emailAddress: onboardingData.email,
          password: onboardingData.password,
        });

        console.log('✅ [TRIAL] Clerk account created:', clerkResult.createdUserId);
        clerkUserId = clerkResult.createdUserId;

        // Sign in the user immediately after account creation
        try {
          console.log('🔄 [TRIAL] Setting active session:', clerkResult.createdSessionId);
          await clerk.setActive({ session: clerkResult.createdSessionId });
          console.log('✅ [TRIAL] User signed in successfully');
          
          // Wait a moment for Clerk state to propagate
          await new Promise(resolve => setTimeout(resolve, 1000));
          console.log('✅ [TRIAL] Authentication state propagation complete');
        } catch (signInError) {
          console.warn('⚠️ [TRIAL] Auto sign-in failed:', signInError);
          console.warn('⚠️ [TRIAL] Attempting alternative sign-in method...');
          
          // Try alternative sign-in approach
          try {
            if (clerkResult.createdSessionId) {
              await clerk.setActive({ 
                session: clerkResult.createdSessionId,
                beforeEmit: () => console.log('🔄 [TRIAL] Setting active session with beforeEmit hook')
              });
              console.log('✅ [TRIAL] Alternative sign-in successful');
              await new Promise(resolve => setTimeout(resolve, 1000));
            }
          } catch (alternativeError) {
            console.error('❌ [TRIAL] Alternative sign-in also failed:', alternativeError);
            console.log('⚠️ [TRIAL] User may need to sign in manually after navigation');
          }
        }

        // Send verification email
        try {
          await signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
          console.log('✅ [TRIAL] Verification email sent');
        } catch (emailError) {
          console.warn('⚠️ [TRIAL] Verification email failed (non-critical):', emailError);
        }
      }

      // Create user profile in Firebase
      console.log('🔄 [TRIAL] Creating Firebase user profile...');
      
      // Prepare payment method data for user profile
      let formattedPaymentMethods = null;
      
      if (paymentMethodData && paymentMethodData.stripePaymentMethodId) {
        const paymentMethodId = paymentMethodData.stripePaymentMethodId;
        const formattedPaymentMethod = {
          paymentMethodId: paymentMethodId,
          email: onboardingData.email.toLowerCase().trim(),
          phoneNumber: onboardingData.phoneNumber,
          fullName: onboardingData.fullName,
          stripeCustomerId: paymentMethodData.stripeCustomerId,
          stripePaymentMethodId: paymentMethodId,
          cardDetails: paymentMethodData.cardDetails,
          isActive: true,
          isDefault: true,
          cardName: "trialCard",
          createdAt: new Date(),
          updatedAt: new Date()
        };
        
        formattedPaymentMethods = {
          [paymentMethodId]: formattedPaymentMethod
        };
        
        console.log('💳 [TRIAL] Prepared payment method for user profile');
      }
      
      const userCreateData = {
        email: onboardingData.email,
        phoneNumber: onboardingData.phoneNumber,
        fullName: onboardingData.fullName,
        clerkUserId: clerkUserId || undefined,
        paymentMethods: formattedPaymentMethods,
        // Also include the Stripe IDs at the top level for backward compatibility
        stripeCustomerId: paymentMethodData?.stripeCustomerId,
        stripePaymentMethodId: paymentMethodData?.stripePaymentMethodId,
        // Trial subscription parameters
        createTrialSubscription: true,
        planId: planKey,
        cardDetails: paymentMethodData?.cardDetails
      };

      console.log('🔄 [TRIAL] User create data prepared:', JSON.stringify(userCreateData, null, 2));

      // Create user profile with payment methods
      console.log('📤 [TRIAL] About to call createUser API...');
      const { createUser } = await import('../../api-v2');
      
      console.log('📞 [TRIAL] Calling createUser with data...');
      const userResult = await createUser(userCreateData);
      
      console.log('📥 [TRIAL] Received user creation result:', JSON.stringify(userResult, null, 2));
      
      if (!userResult || !userResult.success) {
        const errorMsg = userResult?.message || 'Unknown error during user creation';
        console.error('❌ [TRIAL] User creation failed:', errorMsg);
        throw new Error(`Failed to create user profile: ${errorMsg}`);
      }
      
      // Handle both response formats: new backend format { id: "..." } and legacy format { user: { userId: "..." } }
      const userId = userResult.id || userResult.user?.userId || userResult.data?.userId;
      
      if (!userId) {
        console.error('❌ [TRIAL] User created but no userId returned:', userResult);
        throw new Error('User created but no userId was returned from the server');
      }
      
      console.log('✅ [TRIAL] User profile created successfully with userId:', userId);

      // Check if trial subscription was created
      if (userResult.subscription) {
        console.log('✅ [TRIAL] Trial subscription created successfully:', userResult.subscription.subscriptionId);
      } else {
        console.log('⚠️ [TRIAL] User created but no trial subscription data returned');
      }
      
      // Store user data
      localStorage.setItem('userPhone', onboardingData.phoneNumber);
      localStorage.setItem('userEmail', onboardingData.email);
      localStorage.setItem('userFullName', onboardingData.fullName);
      
      // Clear temporary flags
      localStorage.removeItem('phoneVerified');
      localStorage.removeItem('isInSignupFlow');
      
      // Update Redux state
      updateData({
        paymentStatus: 'completed',
        profileCreated: true,
        paidUserDataAdded: true
      });

      console.log('✅ [TRIAL] User accounts created successfully');

    } catch (error: any) {
      console.error('❌ [TRIAL] Error creating accounts:', error);
      setAccountCreationError(error.message || 'Failed to create account. Please try again.');
      setProcessing(false);
      throw error;
    }
  };

  const handleTrialSetup = async () => {
    // Clear any existing errors
    setError('');
    setAccountCreationError('');
    
    if (!stripe || !elements) {
      setError('Payment system not ready. Please try again.');
      return;
    }

    // Validate card information is complete
    if (!cardComplete) {
      setError('Please complete your card information before proceeding.');
      return;
    }

    // Prevent multiple submissions
    if (processing) {
      console.log('⚠️ [TRIAL] Already processing, ignoring additional submission');
      return;
    }

    console.log('🔒 [TRIAL] Setting processing state to true to prevent duplicate submissions');
    setProcessing(true);
    setError('');
    setAccountCreationError('');

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setError('Please enter your card information.');
      setProcessing(false);
      return;
    }

    try {
      console.log('🆓 Starting free trial setup flow...');
      
      // Step 1: Create customer and setup intent for trial (no charge)
      console.log('💳 Step 1: Creating customer and setup intent for trial...');
      
      const normalizedEmail = onboardingData.email.toLowerCase().trim();
      console.log('📧 Using normalized email:', normalizedEmail);
      
      const trialSetupData = await withTimeout(
        createCustomerForTrial(
          normalizedEmail,
          onboardingData.fullName,
          currentPlan.name
        ),
        15000, // 15 second timeout
        'Creating Stripe customer'
      );

      if (!trialSetupData || !trialSetupData.clientSecret) {
        throw new Error('Failed to create trial setup intent');
      }

      console.log('✅ Trial setup intent created:', trialSetupData.customerId);

      // Step 2: Confirm setup intent to save payment method (no charge)
      console.log('💳 Step 2: Confirming setup intent to save card...');
      const { error: setupError, setupIntent } = await withTimeout(
        stripe.confirmCardSetup(
          trialSetupData.clientSecret,
          {
            payment_method: {
              card: cardElement,
              billing_details: {
                name: onboardingData.fullName,
                email: normalizedEmail,
              },
            },
          }
        ),
        15000, // 15 second timeout
        'Confirming card setup with Stripe'
      );

      if (setupError) {
        console.error('❌ Setup intent confirmation failed:', setupError);
        setError(setupError.message || 'Failed to save payment method.');
        setProcessing(false);
        return;
      }

      console.log('✅ Setup intent confirmed, payment method saved');

      // Step 3: Extract payment method details
      const paymentMethod = setupIntent.payment_method;
      
      if (!paymentMethod) {
        throw new Error('Payment method not found in setup intent');
      }
      
      const pmId = typeof paymentMethod === 'string' ? paymentMethod : paymentMethod.id;
      
      // ALWAYS fetch detailed payment method information from backend API
      // SetupIntent doesn't always include complete card details, so we fetch from Stripe directly
      let detailedPaymentMethod = null;
      try {
        console.log('🔄 Fetching detailed payment method from backend API...');
        const paymentMethodDetails = await withTimeout(
          getPaymentMethodDetails(pmId),
          10000, // 10 second timeout
          'Fetching payment method details'
        );
        console.log('🔄 Backend API response:', paymentMethodDetails);
        
        if (paymentMethodDetails.success && paymentMethodDetails.paymentMethod) {
          detailedPaymentMethod = paymentMethodDetails.paymentMethod;
          console.log('✅ Got detailed payment method from backend:', detailedPaymentMethod);
        } else {
          console.warn('⚠️ Backend API did not return payment method details');
          throw new Error('Failed to get payment method details from backend');
        }
      } catch (fetchError) {
        console.error('❌ Failed to fetch payment method details:', fetchError);
        throw new Error(`Could not retrieve card details: ${fetchError instanceof Error ? fetchError.message : String(fetchError)}`);
      }

      // Store payment method data in Redux for later use (without card details for now)
      updateData({
        stripeCustomerId: trialSetupData.customerId,
        paymentMethodId: pmId,
        paymentMethodData: {
          stripePaymentMethodId: pmId,
          stripeCustomerId: trialSetupData.customerId,
          cardDetails: {
            last4: '****', // Will be updated with real details
            brand: 'unknown',
            expMonth: 0,
            expYear: 0,
            cardholderName: onboardingData.fullName
          }
        },
        paidUserDataAdded: true
      });

      console.log('✅ Payment method data stored for trial billing');

      // Step 4: Create user accounts FIRST (pass payment method data)
      console.log('👤 Step 4: Creating user accounts...');
      
      // Extract real card details from Stripe payment method
      console.log('💳 Payment method object from backend:', JSON.stringify(detailedPaymentMethod, null, 2));
      
      // Ensure we have valid card details - NO FALLBACKS or static values
      if (!detailedPaymentMethod || !detailedPaymentMethod.card) {
        console.error('❌ No payment method or card data:', detailedPaymentMethod);
        throw new Error('No card details received from Stripe - payment method may be invalid');
      }
      
      const cardData = detailedPaymentMethod.card;
      console.log('💳 Raw card data from Stripe:', JSON.stringify(cardData, null, 2));
      
      // Check each field individually with detailed logging
      const brand = cardData.brand;
      const last4 = cardData.last4;
      const expMonth = cardData.expMonth; // Backend sends camelCase
      const expYear = cardData.expYear;   // Backend sends camelCase
      
      console.log('💳 Card field analysis:', {
        brand: brand || 'MISSING',
        last4: last4 || 'MISSING',
        expMonth: expMonth || 'MISSING',
        expYear: expYear || 'MISSING'
      });
      
      // More flexible validation - only require brand and last4 as essential
      if (!brand || !last4) {
        throw new Error(`Missing essential card details - brand: ${brand || 'missing'}, last4: ${last4 || 'missing'}`);
      }
      
      const cardDetails = {
        brand: brand,
        last4: last4,
        expMonth: expMonth || 12,  // Use extracted value or default
        expYear: expYear || new Date().getFullYear() + 3,    // Use extracted value or default
        cardholderName: onboardingData.fullName
      };
      
      console.log('💳 FINAL DYNAMIC card details:', cardDetails);
      
      // Validation: Ensure we're not using test card defaults for production
      if (cardDetails.last4 === '4242' && cardDetails.expMonth === 12 && cardDetails.expYear === 2028) {
        console.warn('⚠️ Warning: Using Stripe test card (4242). This should be real card data in production.');
      }
      
      console.log('💳 Final card details:', cardDetails);
      
      const currentPaymentMethodData = {
        stripePaymentMethodId: pmId,
        stripeCustomerId: trialSetupData.customerId,
        cardDetails: cardDetails
      };
      console.log('🔄 About to create user accounts...');
      await withTimeout(
        createUserAccountsAfterSetup(currentPaymentMethodData),
        20000, // 20 second timeout for user creation
        'Creating user accounts'
      );
      console.log('✅ User accounts created successfully');

      // Wait for Firestore eventual consistency
      console.log('⏳ Waiting for user and subscription documents to be fully committed to Firestore...');
      await new Promise(resolve => setTimeout(resolve, 2000)); // Wait for 2 seconds
      console.log('✅ Firestore wait completed');

      // Mark trial setup as completed and navigate
      console.log('🎉 [TRIAL] Trial setup completed successfully!');
      
      // Mark current step as completed
      const newCompletedSteps = [...completedSteps];
      newCompletedSteps[currentStep - 1] = true;
      setCompletedSteps(newCompletedSteps);
      
      // Mark trial setup as successful for Redux state
      updateData({
        paymentStatus: 'completed'
      });
      
      // Complete the onboarding process
      try {
        completeOnboarding();
        console.log('✅ [TRIAL] Onboarding marked as complete in Redux');
      } catch (onboardingError) {
        console.warn('⚠️ [TRIAL] Failed to mark onboarding as complete:', onboardingError);
      }
      
      // Close modal and navigate to dashboard
      setProcessing(false);
      setSetupSuccess(true);
      
      // Verify user is properly authenticated before navigation
      console.log('🔍 [TRIAL] Verifying authentication state before navigation...');
      console.log('🔍 [TRIAL] Current auth state:', { user: !!user, isSignedIn, isLoaded });
      
      // Function to wait for authentication and navigate
      const waitForAuthAndNavigate = async (maxAttempts = 10, currentAttempt = 1) => {
        console.log(`🔍 [TRIAL] Authentication check attempt ${currentAttempt}/${maxAttempts}`);
        console.log('🔍 [TRIAL] Auth state:', { user: !!user, isSignedIn, isLoaded });
        
        if (user && isSignedIn) {
          console.log('✅ [TRIAL] User authenticated, navigating to dashboard');
          console.log('✅ [TRIAL] User details:', { 
            userId: user.id, 
            email: user.emailAddresses?.[0]?.emailAddress,
            currentPath: window.location.pathname 
          });
          
          // Ensure localStorage is set for dashboard access
          if (onboardingData.phoneNumber) {
            localStorage.setItem('userPhone', onboardingData.phoneNumber);
          }
          if (onboardingData.email) {
            localStorage.setItem('userEmail', onboardingData.email);
          }
          if (onboardingData.fullName) {
            localStorage.setItem('userFullName', onboardingData.fullName);
          }
          
          onClose?.();
          navigate('/home', { replace: true });
          return;
        }
        
        if (currentAttempt >= maxAttempts) {
          console.log('⚠️ [TRIAL] Max authentication wait attempts reached, navigating anyway');
          console.log('🔍 [TRIAL] Final auth state before navigation:', { 
            user: !!user, 
            isSignedIn, 
            isLoaded,
            currentPath: window.location.pathname 
          });
          
          // Ensure we're not already on the home page
          if (window.location.pathname !== '/home') {
            onClose?.();
            navigate('/home', { replace: true });
          } else {
            console.log('✅ [TRIAL] Already on home page, closing modal');
            onClose?.();
          }
          return;
        }
        
        // Wait 500ms and try again
        setTimeout(() => {
          waitForAuthAndNavigate(maxAttempts, currentAttempt + 1);
        }, 500);
      };
      
      // Start the authentication check after showing success message
      setTimeout(() => {
        waitForAuthAndNavigate();
      }, 2000); // Show success message for 2 seconds, then navigate

    } catch (error: any) {
      console.error('❌ Error in trial setup:', error);
      console.error('❌ Error stack:', error.stack);
      console.error('❌ Error details:', {
        name: error.name,
        message: error.message,
        code: error.code,
        response: error.response?.data
      });
      setError(error.message || 'Failed to setup trial. Please try again.');
      setProcessing(false);
    }
  };

  const handleGoBack = () => {
    if (isNavigating) return;
    setIsNavigating(true);
    if (setCurrentStep) {
      setCurrentStep(currentStep - 1);
    }
    setTimeout(() => setIsNavigating(false), 100);
  };

  // Only show for step 4 (payment step)
  if (currentStep !== 4) return null;

  if (setupSuccess) {
    return (
      <div className="text-center p-6">
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
          <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">🎉 Free Trial Started!</h3>
        <p className="text-gray-600 mb-4">
          Your 7-day free trial has begun! You'll be redirected to your dashboard in a moment.
        </p>
        <p className="text-sm text-gray-500 mb-2">
          • Trial period: 7 days starting now<br/>
          • Cancel anytime during trial - no charges<br/>
          • After trial: ${(currentPlan.price / 100).toFixed(2)}/{billingCycle === 'yearly' ? 'year' : 'month'}
        </p>
        <p className="text-xs text-gray-400">
          Redirecting to dashboard...
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto p-6">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Start Your Free Trial</h2>
        <p className="text-gray-600">
          Add your payment details to start your 7-day free trial
        </p>
      </div>

      {/* Plan Summary */}
      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        <div className="flex justify-between items-center mb-2">
          <span className="font-medium text-gray-900">{currentPlan.name} Plan</span>
          <span className="text-lg font-bold text-gray-900">
            FREE for 7 days
          </span>
        </div>
        <div className="text-sm text-gray-600 mb-2">
          Then ${(currentPlan.price / 100).toFixed(2)}/{billingCycle === 'yearly' ? 'year' : 'month'}
        </div>
        <div className="text-xs text-gray-500">
          Cancel anytime during trial - no charges
        </div>
      </div>

      {/* Card Input */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Payment Details
        </label>
        <div className="border border-gray-300 rounded-md p-3 bg-white">
          <CardElement
            onChange={handleCardChange}
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  color: '#424770',
                  '::placeholder': {
                    color: '#aab7c4',
                  },
                },
              },
            }}
          />
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Your card will be securely saved for billing after the trial period
        </p>
      </div>

      {/* Error Messages */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {accountCreationError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-600">{accountCreationError}</p>
        </div>
      )}

      {/* Consistent Back/Continue button layout */}
      <div className="flex justify-between items-center mt-8 mb-4">
        <button
          type="button"
          onClick={handleGoBack}
          disabled={processing || isNavigating}
          className="px-6 py-3 text-sm border border-gray-300 text-gray-600 rounded-lg font-medium 
                     hover:border-gray-400 hover:bg-gray-50 transition-all duration-300 
                     flex items-center gap-2 group disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span className="transform group-hover:-translate-x-1 transition-transform duration-300">←</span>
          Back
        </button>
        <button
          type="button"
          onClick={handleTrialSetup}
          disabled={processing || !cardComplete}
          className={`px-6 py-3 rounded-lg text-sm font-medium transition-all duration-300 
                     flex items-center gap-2 group ${
            (!processing && cardComplete)
              ? 'bg-black text-white hover:bg-gray-800 shadow-md hover:shadow-lg'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          {processing ? 'Setting up...' : 'Start Free Trial'}
          <span className="transform group-hover:translate-x-1 transition-transform duration-300">→</span>
        </button>
      </div>

      {/* Terms */}
      <p className="text-xs text-gray-500 text-center mt-4">
        By starting your trial, you agree to our Terms of Service and Privacy Policy. 
        Cancel anytime during the 7-day trial period.
      </p>
    </div>
  );
};

export default TrialPaymentStep;